<template>
  <Sheet>
    <Sheet.Body class="pt-5 px-4!">
      <ShieldIcon class="mx-auto mb-2.5 size-6 stroke-gray-800" />

      <h2
        class="mb-[18px] text-center font-primary text-[20px] font-semibold leading-[26px] text-gray-800"
      >
        Confirmation of right to add work to Dacr
      </h2>

      <p class="px-2 font-secondary text-[11px] leading-[17px] text-gray-800">
        By continuing, I represent and warrant that I own or control the
        copyright (or exclusive rights) in the work(s) submitted, or I am duly
        authorized to submit them for the rights holder, and that Dacr may rely
        on this. Submitting work without rights or providing false information
        may be unlawful (including fraud) and may expose me to civil and
        criminal liability under applicable law.
      </p>

      <div class="my-[14px] h-px w-full bg-[#DFDFDF]" />

      <div
        v-if="originFlagEnabled"
        class="mb-2 rounded-xl border border-[#EEEDEC] bg-[#F6F6F6] px-[13px] pt-[7px] pb-[9px]"
      >
        <div
          class="mb-2 font-secondary text-[13px] font-medium leading-[17px] text-gray-800"
        >
          Jurisdictions to be registered
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div
              class="z-10 -mr-3 flex size-8 items-center justify-center rounded-full border border-gray-600/15 bg-white"
            >
              <DacrMarkIcon class="size-6 text-gray-800" />
            </div>
            <Avatar
              v-for="origin in additionalOrigins"
              :key="origin.id"
              :src="origin.flag.url"
              :size="32"
              class="z-10 -mr-3 border-[3px] border-[#F6F6F6]"
            />
            <button
              v-if="showPlusButton"
              type="button"
              class="flex size-8 items-center justify-center rounded-full border border-dashed border-gray-300/50"
              @click="openJurisdictions"
            >
              <AddIcon class="size-3 text-gray-400" />
            </button>
          </div>
          <button
            type="button"
            class="font-secondary text-[14px] font-medium leading-[18px] text-green-700"
            @click="openJurisdictions"
          >
            Change
          </button>
        </div>
      </div>

      <div
        v-if="showPrices"
        class="flex flex-col gap-y-1.5 rounded-xl border border-[#EEEDEC] px-[13px] py-[7px]"
      >
        <div class="flex items-center justify-between">
          <span class="font-secondary text-[13px] leading-[18px] text-gray-600">
            Works being registered
          </span>
          <span class="font-secondary text-[14px] leading-[18px] text-gray-800">
            {{ quantity }}
          </span>
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-x-2">
            <span
              class="font-secondary text-[13px] font-medium leading-[18px] text-gray-800"
            >
              Total to be billed
            </span>
            <button
              type="button"
              class="font-secondary text-[13px] font-medium leading-[18px] text-green-700"
              @click="openDetails"
            >
              Details
            </button>
          </div>
          <span
            class="font-secondary text-[17px] font-medium leading-[18px] text-gray-800"
          >
            {{ totalCost }}
          </span>
        </div>
      </div>

      <div class="mt-[14px] flex items-start gap-x-2 mb-5">
        <Checkbox
          v-model="agreeToTerms"
          :class="['mt-px shrink-0', { 'cb-attention': !agreeToTerms }]"
        />
        <p
          class="cursor-pointer font-secondary text-[12px] leading-[17px] text-gray-600"
          @click="agreeToTerms = !agreeToTerms"
        >
          I represent and warrant I have the rights/authorization to submit
          these work(s), authorize Dacr to charge the amounts shown above to my
          card on file, and agree to the
          <a
            class="text-green-700 font-medium"
            href="/legal/terms"
            target="_blank"
            @click.stop
            >Dacr Terms</a
          >,
          <a
            class="text-green-700 font-medium"
            href="/legal/privacy"
            target="_blank"
            @click.stop
            >Privacy Policy</a
          >, and
          <a
            class="text-green-700 font-medium"
            href="/legal/payments"
            target="_blank"
            @click.stop
            >Payments Policy</a
          >. All fees are final except where required by law.
        </p>
      </div>

      <div class="flex flex-col items-center gap-2">
        <Button
          variant="primary"
          size="lg"
          class="w-full"
          :disabled="disableButton"
          @click="handleAgree"
        >
          Agree, continue
        </Button>
        <button
          type="button"
          class="font-secondary text-[14px] font-medium text-green-700"
          @click="handleCancel"
        >
          Cancel upload
        </button>
      </div>
    </Sheet.Body>
  </Sheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Sheet, Button, Checkbox, Avatar, useSheet } from '@dacr/library'
import { DacrMarkIcon, AddIcon } from '@dacr/icons'
import ShieldIcon from '@/assets/svg/icons/ShieldIcon.svg'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
import { useUploadStore } from '@/features/upload/store'
import { useOriginStore } from '@/shared/stores/origin'
import { useBillingStore } from '@/features/billing/store'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import { PlanType } from '@/shared/graphql/legacy/generated'
import { formatPrice } from '@/shared/utils/money'
import gql from 'graphql-tag'
import { useQuery } from '@vue/apollo-composable'
import type {
  EstimateRegistrationCostQuery,
  EstimateRegistrationCostQueryVariables,
} from '@/shared/graphql/router/graphql'

const { closeAll, triggerEvent } = useSheet()
const uploadStore = useUploadStore()
const originStore = useOriginStore()
const billingStore = useBillingStore()
const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const draft = computed(() => uploadStore.draft)
const quantity = computed(() => draft.value?.files.length ?? 0)
const originIds = computed(() => draft.value?.originIds ?? ['Dacr'])

const additionalOrigins = computed(() =>
  originStore.origins.filter(
    (origin) => origin.id !== 'Dacr' && originIds.value.includes(origin.id)
  )
)

const showPlusButton = computed(
  () => originIds.value.length === 1 && originFlagEnabled.value
)

const showPrices = computed(
  () => billingStore.billing?.plan.type === PlanType.Pro
)

const ESTIMATE_REGISTRATION_COST = gql`
  query EstimateRegistrationCost($input: EstimateRegistrationCostInput!) {
    estimateRegistrationCost(input: $input) {
      total {
        amount
        formatted
      }
      breakdown {
        description
        origin {
          id
          name
          flag {
            url
          }
        }
        total {
          amount
          formatted
        }
        breakdown {
          id
          name
          total {
            amount
            formatted
          }
        }
      }
    }
  }
`

const { result: estimateResult } = useQuery<
  EstimateRegistrationCostQuery,
  EstimateRegistrationCostQueryVariables
>(
  ESTIMATE_REGISTRATION_COST,
  () => ({
    input: {
      origins: [...originIds.value],
      quantity: quantity.value,
    },
  }),
  { clientId: 'router' }
)

const estimatedCost = computed(
  () => estimateResult.value?.estimateRegistrationCost ?? null
)

const totalCost = computed(() =>
  formatPrice({ amount: estimatedCost.value?.total.amount || 0 })
)

const agreeToTerms = ref(false)

const disableButton = computed(
  () => !agreeToTerms.value || (showPrices.value && !estimatedCost.value)
)

const openJurisdictions = () =>
  openSheet(Sheets.UploadJurisdictions, { stack: 'cover' })

const openDetails = () =>
  openSheet(Sheets.UploadPricingDetails, {
    stack: 'cover',
    props: { estimatedCost: estimatedCost.value },
  })

const handleAgree = () => triggerEvent('success')

const handleCancel = () => {
  uploadStore.clearDraft()
  closeAll()
}
</script>

<style scoped>
.cb-attention :deep(span) {
  border-color: var(--color-green-500);
  box-shadow: 0 0 0 2px
    color-mix(in srgb, var(--color-green-200) 50%, transparent);
}
</style>
